import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { useParams } from 'react-router-dom';
import { RootState } from '../../../reducers';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Collapse,
    createTheme,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid,
    Input,
    InputLabel,
    MenuItem,
    Modal,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Stack,
    TextField,
    Typography,
    Tooltip,
} from '@mui/material';
import { gotoNextMenu, gotoPreviousMenu } from '../../../store/assessment.slice';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import AlertDialog from '../../AlertDialog/AlertDialog';
import { copyObject, highlightStyle, isDefaultDate } from '../../../utils/common';
import { StatusEnum } from '../../../Enum/StatusEnum';

import BHDiagnosticConfirmationBody from '../../Shared/BHDiagnosticConfirmationBody';
import {
    fetchMentalHealthTreatmentHistoryInfo,
    fetchMentalHealthTreatmentHistoryInfoByMemberId,
    saveConfirmBHTherapists,
    saveMentalHealthTreatmentHistoryInfo,
    setMentalHealthTreatmentHistoryInfo,
    setSelectedTherapistIds,
} from '../../../store/mentalHealthTreatmentHistorySlice.slice';
import { modalStyle, NO, YES } from '../../../utils/constants';
import { MentalHealthTreatmentHistoryModel } from '../../../Models/MentalHealthTreatmentHistoryModel/MentalHealthTreatmentHistoryModel.model';
import { deletePcpSpecialist, fetchPcpAndSpecialists, fetchSpecialistTypes, savePcpSpecialist } from '../../../store/memberDetail.slice';

import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { HospitalizationModel } from '../../../Models/MentalHealthTreatmentHistoryModel/HospitalizationModel.model';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { localDateFormat } from '../../../utils/timeFormat';
import { fetchAllFacilities, fetchPreferredContactOptions } from '../../../store/shared.slice';
import TextMaskCustom from '../../Shared/PhoneMask';
import { DiagnosticType } from '../../../Enum/DiagnosticType';
import { toDate, formatDate } from '../../../utils/dateHelpers';
import { AssessmentQuestions } from '../../../utils/assessmentQuestions';
import { isAIIcon } from '../Assessment';
import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/Assessment_Abuse_History_Developmental_History_Mental_Health_Treatment_History.pdf';

const theme = createTheme();
const classes = {
    form: {
        '& .MuiCheckbox-root': {
            marginLeft: '-10px',
        },
    },
    root: {
        '& .MuiGrid-root': {
            marginBottom: theme.spacing(2),
        },
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
            width: '90%',
        },
    },
};

const yesNoList = [{ Answer: YES }, { Answer: NO }];

const MentalHealthTreatmentHistory: React.FC<{ memberId: string }> = ({ memberId }) => {
    const params = useParams();
    memberId = params.memberId;
    const dispatch = useAppDispatch();
    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
    const { engagementDraft, memberAssessmentHistory, isSaving: isSavingEngagementDraft } = useSelector((state: RootState) => state.documentEngagement);
    const isBHDiagnostic = engagementDraft.DiagnosticStatus?.DiagnosticType === DiagnosticType.BHDiagnostic;
    const { mentalHealthTreatmentHistoryInfo, selectedTherapistIds } = useSelector((state: RootState) => state.mentalHealthTreatmentHistorySlice);
    const [loadedMemberId, setLoadedMemberId] = useState<string | null>(null);
    const { allFacilities, preferredContactOptions } = useSelector((state: RootState) => state.shared);

    const [facilityInputValue, setFacilityInputValue] = useState('');
    const [hospitalizationForm, setHospitalizationForm] = useState(new HospitalizationModel());
    const [showModal, setShowModal] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [openConfirmModal, setOpenConfirmModal] = useState(false);
    const [isChanged, setIsChanged] = useState<boolean>(false);

    const getDefaultTherapist = () => {
        return {
            Id: null,
            ClinicName: '',
            Name: '',
            Address: '',
            Email: '',
            OfficePhone: '',
            FaxNumber: '',
            PcpOrSpecialistType: 2,
            SpecialistType: 19,
            PreferredContact: 2,
            MobilePhone: '',
        };
    };

    const [showTherapistModal, setShowTherapistModal] = useState(false);
    const [showDeleteTherapist, setShowDeleteTherapist] = useState(false);
    const [therapistForm, setTherapistForm] = useState(getDefaultTherapist());

    const mentalHealthTreatmentHistoryInfoIsComplete = (mentalHealthTreatmentHistoryInfo: MentalHealthTreatmentHistoryModel) => {
        const isComplete = Boolean(
            mentalHealthTreatmentHistoryInfo.AdmittedToTreatmentFacility &&
                (mentalHealthTreatmentHistoryInfo.AssessedForSMI === YES ||
                    (mentalHealthTreatmentHistoryInfo.AssessedForSMI === NO && mentalHealthTreatmentHistoryInfo.AssessedForSMIComments)) &&
                (mentalHealthTreatmentHistoryInfo.RecievedMentalHealthTreatment === NO ||
                    (mentalHealthTreatmentHistoryInfo.RecievedMentalHealthTreatment === YES &&
                        mentalHealthTreatmentHistoryInfo.ReasonForTreatment &&
                        mentalHealthTreatmentHistoryInfo.IsTherapyHelpful &&
                        mentalHealthTreatmentHistoryInfo.MemberComments))
        );
        return isComplete;
    };

    const isSaving = isSavingEngagementDraft;
    const { specialistList, specialistTypes } = useSelector((state: RootState) => state.memberDetail);

    // Confirm BH Therapist Start
    const therapistColumns: GridColDef[] = [
        {
            field: 'ClinicName',
            headerName: 'Clinic Name',
            flex: 1,
            minWidth: 200,
        },
        {
            field: 'Name',
            headerName: 'Provider Name',
            flex: 1,
            minWidth: 200,
        },
        {
            field: 'SpecialistType',
            headerName: 'Specialist Type',
            minWidth: 150,
            valueGetter: (value, row) => {
                let found = specialistTypes.find((x) => x.value === row.SpecialistType.toString());
                return found ? found.label : '';
            },
        },
        {
            field: 'Address',
            headerName: 'Address',
            minWidth: 200,
            flex: 1,
        },
        {
            field: 'MobilePhone',
            headerName: 'Mobile Phone',
            minWidth: 150,
            renderCell: (params) => {
                return <Input readOnly value={params.row.MobilePhone} name="textmask" id="formatted-text-mask-input" inputComponent={TextMaskCustom as any} />;
            },
        },
        {
            field: 'OfficePhone',
            headerName: 'Office Phone',
            minWidth: 150,
            renderCell: (params) => {
                return <Input readOnly value={params.row.OfficePhone} name="textmask" id="formatted-text-mask-input" inputComponent={TextMaskCustom as any} />;
            },
        },
        {
            field: 'Email',
            headerName: 'Email',
            minWidth: 200,
        },
        {
            field: 'action',
            headerName: 'Action',
            minWidth: 100,
            sortable: false,
            renderCell: (params) => {
                return (
                    <>
                        <Edit
                            className="icon-left"
                            onClick={() => {
                                addTherapistHandler(params.row);
                            }}
                        />
                        <Delete
                            className="icon-right"
                            onClick={() => {
                                deleteTherapistHandler(params.row);
                            }}
                        />
                    </>
                );
            },
        },
    ];

    const openConfirmationHandler = () => {
        dispatch(setSelectedTherapistIds([]));
        setOpenConfirmModal(true);
    };

    const resetTherapist = (row: any) => {
        let update = row ? { ...row } : getDefaultTherapist();
        setTherapistForm(update);
    };

    const addTherapistHandler = (row: any = null) => {
        resetTherapist(row);
        setShowTherapistModal(true);
    };

    const deleteTherapistHandler = (row: any) => {
        resetTherapist(row);
        setShowDeleteTherapist(true);
        // setShowDelete(true);
    };

    const confirmDeleteTherapistHandler = () => {
        dispatch(deletePcpSpecialist(memberId, therapistForm.Id));
        setShowDeleteTherapist(false);
    };

    const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>, prop) => {
        let update = { ...therapistForm };
        update[prop] = event.target.value;
        setTherapistForm(update);
    };

    const saveTherapist = () => {
        let update = copyObject(therapistForm);
        update.MobilePhone = therapistForm.MobilePhone?.replace(/[^\d.]/g, '');
        update.OfficePhone = therapistForm.OfficePhone?.replace(/[^\d.]/g, '');
        update.FaxNumber = therapistForm.FaxNumber?.replace(/[^\d.]/g, '');
        dispatch(savePcpSpecialist(update));
        setShowTherapistModal(false);
    };

    const closeConfirmTherapistHandler = () => {
        setOpenConfirmModal(false);
    };

    const saveConfirmTherapistHandler = () => {
        const selectedTherapists = selectedTherapistIds.map((id) => {
            return specialistList.find((x) => x.Id === id);
        });
        const confirmedTherapist = selectedTherapists;
        const historyCopy = copyObject(mentalHealthTreatmentHistoryInfo);
        historyCopy.TherapistName = confirmedTherapist.map((x) => x.Name).join(', ');
        historyCopy.ClinicName = confirmedTherapist.map((x) => x.ClinicName).join(', ');
        dispatch(setMentalHealthTreatmentHistoryInfo(historyCopy));
        dispatch(saveConfirmBHTherapists(memberId, confirmedTherapist));
        closeConfirmTherapistHandler();
    };

    const ConfirmTherapistModal = () => {
        return (
            <Dialog
                open={openConfirmModal}
                onClose={closeConfirmTherapistHandler}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth
                maxWidth="xl"
            >
                <DialogTitle id="alert-dialog-title">Confirm BH Therapist</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Button variant="contained" fullWidth sx={{ width: '120px' }} onClick={() => addTherapistHandler()}>
                            Add
                        </Button>
                        <DataGrid
                            autoHeight
                            getRowId={(row) => row.Id}
                            rows={copyObject(specialistList).sort((a, b) => (a.SpecialistType !== 19 ? 1 : -1))}
                            columns={therapistColumns}
                            checkboxSelection={true}
                            disableRowSelectionOnClick={true}
                            onRowSelectionModelChange={(newRowSelectionModel) => {
                                const idsArray = Array.isArray(newRowSelectionModel)
                                    ? newRowSelectionModel
                                    : newRowSelectionModel && 'ids' in newRowSelectionModel
                                    ? Array.from(newRowSelectionModel.ids)
                                    : [];
                                dispatch(setSelectedTherapistIds(idsArray));
                            }}
                            rowSelectionModel={
                                Array.isArray(selectedTherapistIds) && selectedTherapistIds.length > 0
                                    ? { type: 'include' as const, ids: new Set(selectedTherapistIds) }
                                    : undefined
                            }
                            hideFooter
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button className="button-120" variant="outlined" onClick={closeConfirmTherapistHandler}>
                        Cancel
                    </Button>
                    <Button
                        className="button-120"
                        disabled={selectedTherapistIds.length === 0 || isSaving}
                        variant="contained"
                        onClick={saveConfirmTherapistHandler}
                        autoFocus
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const renderTherapistBody = () => {
        return (
            <Stack spacing={2}>
                <Stack spacing={3}>
                    <FormGroup>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                id="outlined-basic"
                                fullWidth
                                required
                                error={Boolean(therapistForm.Name === '')}
                                label="Provider Name"
                                variant="outlined"
                                value={therapistForm.Name}
                                onChange={(e: any) => {
                                    setTherapistForm({ ...therapistForm, Name: e.target.value });
                                }}
                            />
                            <TextField
                                id="outlined-basic"
                                fullWidth
                                label="Clinic Name"
                                variant="outlined"
                                value={therapistForm.ClinicName}
                                onChange={(e: any) => {
                                    setTherapistForm({ ...therapistForm, ClinicName: e.target.value });
                                }}
                            />
                        </Stack>
                    </FormGroup>
                    <FormGroup>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                id="outlined-basic"
                                fullWidth
                                label="Address"
                                variant="outlined"
                                value={therapistForm.Address}
                                onChange={(e: any) => {
                                    setTherapistForm({ ...therapistForm, Address: e.target.value });
                                }}
                            />
                            <FormControl fullWidth>
                                <InputLabel id="demo-simple-select-label">Specialist Type</InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    value={therapistForm.SpecialistType}
                                    label="Specialist Type"
                                    onChange={(e: any) => {
                                        setTherapistForm({ ...therapistForm, SpecialistType: e.target.value });
                                    }}
                                >
                                    {specialistTypes.map(function (option) {
                                        return (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Stack>
                    </FormGroup>
                    <FormGroup>
                        <Stack direction="row" spacing={2}>
                            <FormControl variant="standard" fullWidth>
                                <InputLabel htmlFor="formatted-text-mask-input">Mobile Phone</InputLabel>
                                <Input
                                    value={therapistForm.MobilePhone}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        handlePhoneChange(e, 'MobilePhone');
                                    }}
                                    name="textmask"
                                    id="formatted-text-mask-input"
                                    inputComponent={TextMaskCustom as any}
                                />
                            </FormControl>
                            <FormControl variant="standard" fullWidth>
                                <InputLabel htmlFor="formatted-text-mask-input">Office Phone</InputLabel>
                                <Input
                                    value={therapistForm.OfficePhone}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        handlePhoneChange(e, 'OfficePhone');
                                    }}
                                    name="textmask"
                                    id="formatted-text-mask-input"
                                    inputComponent={TextMaskCustom as any}
                                />
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel id="demo-simple-select-label">Preferred Contact</InputLabel>
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    value={therapistForm.PreferredContact}
                                    label="Preferred Contact"
                                    onChange={(e: any) => {
                                        setTherapistForm({ ...therapistForm, PreferredContact: e.target.value });
                                    }}
                                >
                                    {preferredContactOptions.map(function (option) {
                                        return (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Stack>
                    </FormGroup>
                    <FormGroup>
                        <Stack direction="row" spacing={2}>
                            <FormControl variant="standard" fullWidth>
                                <InputLabel htmlFor="formatted-text-mask-input">Fax Number</InputLabel>
                                <Input
                                    value={therapistForm.FaxNumber}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        handlePhoneChange(e, 'FaxNumber');
                                    }}
                                    name="textmask"
                                    id="formatted-text-mask-input"
                                    inputComponent={TextMaskCustom as any}
                                />
                            </FormControl>
                            <TextField
                                id="outlined-basic"
                                fullWidth
                                label="Email"
                                variant="outlined"
                                value={therapistForm.Email}
                                onChange={(e: any) => {
                                    setTherapistForm({ ...therapistForm, Email: e.target.value });
                                }}
                            />
                        </Stack>
                    </FormGroup>
                </Stack>
                <Box>
                    <Stack direction="row" spacing={2} style={{ float: 'right' }}>
                        <Button disabled={therapistForm.Name === ''} className="button-120" variant="contained" onClick={saveTherapist}>
                            Save
                        </Button>
                        <Button
                            className="button-120"
                            variant="outlined"
                            onClick={() => {
                                setShowTherapistModal(false);
                            }}
                        >
                            Cancel
                        </Button>
                    </Stack>
                </Box>
            </Stack>
        );
    };

    const renderDeleteTherapistBody = () => {
        return (
            <Stack spacing={2}>
                <Typography sx={{ marginLeft: 3 }} variant="h6">
                    Are you sure do you want to delete: {therapistForm.Name}?
                </Typography>
                <Box>
                    <Stack direction="row" spacing={2} style={{ float: 'right' }}>
                        <Button className="button-120" variant="contained" color="error" onClick={() => confirmDeleteTherapistHandler()}>
                            Delete
                        </Button>
                        <Button
                            className="button-120"
                            variant="outlined"
                            onClick={() => {
                                setShowDeleteTherapist(false);
                            }}
                        >
                            Cancel
                        </Button>
                    </Stack>
                </Box>
            </Stack>
        );
    };

    // Confirm BH Therapist End

    // Hospitalization Start

    const hospitalizationColumns: GridColDef[] = [
        {
            field: 'Facility',
            headerName: 'Facility',
            flex: 1,
            minWidth: 200,
            valueGetter: (value, row) => `${row.Facility?.Name || ''}`,
        },
        {
            field: 'StartDate',
            headerName: 'Start Date',
            minWidth: 100,
            valueGetter: (value, row) => `${isDefaultDate(row.StartDate) ? '-' : localDateFormat(new Date(row.StartDate))}`,
        },
        {
            field: 'EndDate',
            headerName: 'End Date',
            minWidth: 100,
            valueGetter: (value, row) => `${isDefaultDate(row.EndDate) ? '-' : localDateFormat(new Date(row.EndDate))}`,
        },
        {
            field: 'ReasonForHospitalization',
            headerName: 'Reason',
            minWidth: 200,
            flex: 1,
        },
        {
            field: 'AdditionalDetails',
            headerName: 'Additional Details',
            minWidth: 200,
            flex: 1,
        },
        {
            field: 'action',
            headerName: 'Action',
            minWidth: 100,
            sortable: false,
            renderCell: (params) => {
                return (
                    <>
                        <Edit
                            className="icon-left"
                            onClick={() => {
                                addHospitalizationHandler(params.row);
                                setShowModal(true);
                            }}
                        />
                        <Delete
                            className="icon-right"
                            onClick={() => {
                                setHospitalizationForm(params.row);
                                setShowDelete(true);
                            }}
                        />
                    </>
                );
            },
        },
    ];

    const saveHospitalization = () => {
        const formCopy = copyObject(hospitalizationForm);
        formCopy.MemberId = memberId;
        const historyCopy = copyObject(mentalHealthTreatmentHistoryInfo);
        historyCopy.MemberHospitalizations = historyCopy.MemberHospitalizations || [];
        if (formCopy.Id) {
            const index = historyCopy.MemberHospitalizations.findIndex((hospitalization) => hospitalization.Id === formCopy.Id);
            if (index > -1) {
                historyCopy.MemberHospitalizations[index] = formCopy;
            } else {
                historyCopy.MemberHospitalizations.push(formCopy);
            }
        } else {
            historyCopy.MemberHospitalizations.push(formCopy);
        }
        dispatch(setMentalHealthTreatmentHistoryInfo(historyCopy));
        setShowModal(false);
    };

    const addHospitalizationHandler = (row: any = null) => {
        setHospitalizationForm(new HospitalizationModel());
        if (row) {
            setHospitalizationForm(row);
        }
        setFacilityInputValue(row?.Facility?.Name || '');

        setShowModal(true);
    };

    const deleteHospitalizationHandler = () => {
        const historyCopy = copyObject(mentalHealthTreatmentHistoryInfo);
        const index = historyCopy.MemberHospitalizations.findIndex((hospitalization) => hospitalization.Id === hospitalizationForm.Id);
        historyCopy.MemberHospitalizations[index].IsDeleted = true;
        dispatch(setMentalHealthTreatmentHistoryInfo(historyCopy));
        setShowDelete(false);
    };

    const RenderHospitalizationBody = () => {
        return (
            <Stack spacing={2}>
                <FormGroup>
                    <Stack spacing={2}>
                        <FormControl fullWidth>
                            <Autocomplete
                                disablePortal
                                fullWidth
                                id="facility-auto-complete"
                                options={allFacilities}
                                value={hospitalizationForm.Facility || null}
                                onChange={(e, value) => {
                                    setHospitalizationForm({ ...hospitalizationForm, Facility: value });
                                }}
                                isOptionEqualToValue={(option, value) => option.Name === value.Name && option.Id === value.Id}
                                inputValue={facilityInputValue}
                                onInputChange={(event, newInputValue) => {
                                    setFacilityInputValue(newInputValue);
                                }}
                                sx={{ marginY: 2 }}
                                getOptionLabel={(option) =>
                                    option
                                        ? option.Name?.toUpperCase() +
                                          ' - ' +
                                          option.Address?.toUpperCase() +
                                          ' ' +
                                          option.City?.toUpperCase() +
                                          ' ' +
                                          option.State?.toUpperCase() +
                                          ' ' +
                                          option.ZipCode
                                        : ''
                                }
                                renderOption={(props, option) => {
                                    return (
                                        <li
                                            {...props}
                                            key={option.Id}
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                alignItems: 'flex-start',
                                            }}
                                        >
                                            <Typography variant="body1">{option.Name}</Typography>
                                            <Typography variant="body2" color={'text.secondary'}>
                                                {option.Address} {option.City}, {option.State} {option.ZipCode}
                                            </Typography>
                                        </li>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        inputProps={{ ...params.inputProps, height: 600 }}
                                        helperText="Start typing to select a facility from the list"
                                        label="Facility"
                                    />
                                )}
                            />
                        </FormControl>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <Stack direction="row" spacing={2}>
                                <DatePicker
                                    label="Start Date"
                                    value={hospitalizationForm.StartDate || null}
                                    onChange={(newValue) =>
                                        setHospitalizationForm({
                                            ...hospitalizationForm,
                                            StartDate: newValue,
                                        })
                                    }
                                    slotProps={{ textField: { margin: 'dense' } }}
                                />
                                <DatePicker
                                    label="End Date"
                                    value={hospitalizationForm.EndDate || null}
                                    onChange={(newValue) =>
                                        setHospitalizationForm({
                                            ...hospitalizationForm,
                                            EndDate: newValue,
                                        })
                                    }
                                    slotProps={{ textField: { margin: 'dense' } }}
                                />
                            </Stack>
                        </LocalizationProvider>

                        <TextField
                            id="outlined-basic"
                            fullWidth
                            label="Reason for hospitalization"
                            variant="outlined"
                            value={hospitalizationForm.ReasonForHospitalization}
                            onChange={(e: any) => {
                                setHospitalizationForm({ ...hospitalizationForm, ReasonForHospitalization: e.target.value });
                            }}
                        />
                        <TextField
                            id="outlined-basic"
                            fullWidth
                            label="Additional Details"
                            variant="outlined"
                            value={hospitalizationForm.AdditionalDetails}
                            onChange={(e: any) => {
                                setHospitalizationForm({ ...hospitalizationForm, AdditionalDetails: e.target.value });
                            }}
                        />
                    </Stack>
                </FormGroup>
                <Box>
                    <Stack direction="row" spacing={2} style={{ float: 'right' }}>
                        <Button className="button-120" variant="contained" onClick={saveHospitalization}>
                            {hospitalizationForm.Id ? 'Update' : 'Save'}
                        </Button>
                        <Button
                            className="button-120"
                            variant="outlined"
                            onClick={() => {
                                setShowModal(false);
                            }}
                        >
                            Cancel
                        </Button>
                    </Stack>
                </Box>
            </Stack>
        );
    };

    const RenderDeleteHospitalizationBody = () => {
        return (
            <Stack spacing={2}>
                <Typography sx={{ marginLeft: 3 }} variant="h6">
                    Are you sure do you want to delete: {hospitalizationForm.Facility?.Name}?
                </Typography>
                <Box>
                    <Stack direction="row" spacing={2} style={{ float: 'right' }}>
                        <Button className="button-120" variant="contained" color="error" onClick={deleteHospitalizationHandler}>
                            Delete
                        </Button>
                        <Button
                            className="button-120"
                            variant="outlined"
                            onClick={() => {
                                setShowDelete(false);
                            }}
                        >
                            Cancel
                        </Button>
                    </Stack>
                </Box>
            </Stack>
        );
    };

    // Hospitalization End

    const handleSubmit = () => {
        setShowSaveAlert(true);
    };

    const handleOnConfirmToNext = (mentalHealthTreatmentHistoryInfo: MentalHealthTreatmentHistoryModel, isComplete = false) => {
        const copy = copyObject(mentalHealthTreatmentHistoryInfo);
        copy.MemberId = memberId;
        copy.IsAssessmentCompleted = isComplete;
        if (Boolean(isComplete)) {
            copy.AssessmentStatus = StatusEnum.Completed;
            dispatch(saveMentalHealthTreatmentHistoryInfo(copy, false, true));
        } else {
            copy.AssessmentStatus = StatusEnum.InProgress;
            dispatch(saveMentalHealthTreatmentHistoryInfo(copy, true, true));
        }
    };

    const handleOnConfirmSkip = () => {
        dispatch(setMentalHealthTreatmentHistoryInfo(new MentalHealthTreatmentHistoryModel()));
        dispatch(gotoNextMenu());
    };

    useEffect(() => {
        if (engagementDraft?.MentalHealthTreatmentHistoryId && loadedMemberId !== engagementDraft.MentalHealthTreatmentHistoryId) {
            dispatch(fetchMentalHealthTreatmentHistoryInfo(engagementDraft.MentalHealthTreatmentHistoryId));
            setLoadedMemberId(engagementDraft.MentalHealthTreatmentHistoryId);
        } else if (!engagementDraft?.MentalHealthTreatmentHistoryId && loadedMemberId !== memberId) {
            dispatch(fetchMentalHealthTreatmentHistoryInfoByMemberId(memberId));
            setLoadedMemberId(memberId);
        }
    }, [engagementDraft?.MentalHealthTreatmentHistoryId, memberId, dispatch, loadedMemberId]);

    useEffect(() => {
        dispatch(fetchPcpAndSpecialists(memberId));
    }, [memberId, dispatch]);

    useEffect(() => {
        dispatch(fetchSpecialistTypes());
        dispatch(fetchAllFacilities());
        dispatch(fetchPreferredContactOptions());
    }, [dispatch]);

    return (
        <>
            <Modal
                open={showDelete}
                onClose={() => {
                    setShowDelete(false);
                }}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={modalStyle}>
                    <Stack spacing={2}>
                        <Typography variant="h5">Delete Hospitalization</Typography>
                        <Divider />
                        {RenderDeleteHospitalizationBody()}
                    </Stack>
                </Box>
            </Modal>
            <Modal
                open={showModal}
                onClose={() => {
                    setShowModal(false);
                }}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={modalStyle}>
                    <Stack spacing={2}>
                        <Typography variant="h5">Hospitalization</Typography>
                        <Divider />
                        {RenderHospitalizationBody()}
                    </Stack>
                </Box>
            </Modal>
            <Modal
                open={showTherapistModal}
                onClose={() => {
                    setShowTherapistModal(false);
                }}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={modalStyle}>
                    <Stack spacing={2}>
                        <Typography variant="h5">Specialist</Typography>
                        <Divider />
                        {renderTherapistBody()}
                    </Stack>
                </Box>
            </Modal>
            <Modal
                open={showDeleteTherapist}
                onClose={() => {
                    setShowDeleteTherapist(false);
                }}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={modalStyle}>
                    <Stack spacing={2}>
                        <Typography variant="h5">Delete Therapist</Typography>
                        <Divider />
                        {renderDeleteTherapistBody()}
                    </Stack>
                </Box>
            </Modal>
            {ConfirmTherapistModal()}
            <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
                <Grid container sx={classes.form} direction="column" spacing={2} className="content-panel">
                    <Grid>
                        {isSaving ? (
                            <Box display="flex" justifyContent="center" className="loader-container">
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                <Grid container spacing={2} sx={classes.root}>
                                    <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                        <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                            <Typography variant="h6">Mental Health Treatment History</Typography>
                                            <Tooltip title="Mental Health Treatment History Help Document">
                                                <InfoIcon
                                                    color="primary"
                                                    fontSize="small"
                                                    cursor="pointer"
                                                    onClick={() => window.open(`${HelpPage}`, '_blank')}
                                                />
                                            </Tooltip>
                                        </Stack>
                                        {isBHDiagnostic && (
                                            <Typography variant="body2" component="label" ml={5} sx={highlightStyle}>
                                                * Required for BH Diagnostic
                                            </Typography>
                                        )}
                                        <hr />
                                    </Grid>
                                    <Grid size={6}>
                                        <FormLabel sx={isBHDiagnostic ? highlightStyle : undefined} required={isBHDiagnostic}>
                                            {AssessmentQuestions.MentalHealthTreatmentHistory_HistoryOfMentalHealthTreatment}
                                            {mentalHealthTreatmentHistoryInfo.IsRecievedMentalHealthTreatmentAi && isAIIcon()}
                                        </FormLabel>
                                    </Grid>
                                    <Grid size={6}>
                                        <RadioGroup
                                            row
                                            name="RecievedMentalHealthTreatment"
                                            value={mentalHealthTreatmentHistoryInfo.RecievedMentalHealthTreatment || ''}
                                            onChange={(e) => {
                                                dispatch(
                                                    setMentalHealthTreatmentHistoryInfo({
                                                        ...mentalHealthTreatmentHistoryInfo,
                                                        RecievedMentalHealthTreatment: e.target.value,
                                                        IsRecievedMentalHealthTreatmentAi: false,
                                                    })
                                                );
                                                if (e.target.value === YES) {
                                                    openConfirmationHandler();
                                                }
                                                setIsChanged(true);
                                            }}
                                        >
                                            {yesNoList.map((type, index) => {
                                                return (
                                                    <FormControlLabel
                                                        key={`answer-label-${index}`}
                                                        value={type.Answer}
                                                        control={<Radio />}
                                                        label={type.Answer}
                                                    />
                                                );
                                            })}
                                        </RadioGroup>
                                    </Grid>
                                    <Grid size={12}>
                                        <Collapse
                                            in={mentalHealthTreatmentHistoryInfo.RecievedMentalHealthTreatment === YES}
                                            timeout="auto"
                                            unmountOnExit
                                            sx={{ width: '100%' }}
                                        >
                                            <Grid container>
                                                <Grid size={6}>
                                                    <FormLabel>{AssessmentQuestions.MentalHealthTreatmentHistory_TherapistName}</FormLabel>
                                                </Grid>
                                                <Grid size={6}>
                                                    <TextField
                                                        label={AssessmentQuestions.MentalHealthTreatmentHistory_TherapistName}
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        margin="normal"
                                                        name="TherapistName"
                                                        value={mentalHealthTreatmentHistoryInfo.TherapistName || ''}
                                                        onChange={(e) => {
                                                            dispatch(
                                                                setMentalHealthTreatmentHistoryInfo({
                                                                    ...mentalHealthTreatmentHistoryInfo,
                                                                    TherapistName: e.target.value,
                                                                    IsTherapistNameAi: false,
                                                                })
                                                            );
                                                            setIsChanged(true);
                                                        }}
                                                        InputProps={{
                                                            endAdornment: mentalHealthTreatmentHistoryInfo.IsTherapistNameAi && isAIIcon(),
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid size={6}>
                                                    <FormLabel>{AssessmentQuestions.MentalHealthTreatmentHistory_ClinicName}</FormLabel>
                                                </Grid>
                                                <Grid size={6}>
                                                    <TextField
                                                        label={AssessmentQuestions.MentalHealthTreatmentHistory_ClinicName}
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        margin="normal"
                                                        name="ClinicName"
                                                        value={mentalHealthTreatmentHistoryInfo.ClinicName || ''}
                                                        onChange={(e) => {
                                                            dispatch(
                                                                setMentalHealthTreatmentHistoryInfo({
                                                                    ...mentalHealthTreatmentHistoryInfo,
                                                                    ClinicName: e.target.value,
                                                                    IsClinicNameAi: false,
                                                                })
                                                            );
                                                            setIsChanged(true);
                                                        }}
                                                        InputProps={{
                                                            endAdornment: mentalHealthTreatmentHistoryInfo.IsClinicNameAi && isAIIcon(),
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid size={6}>
                                                    <FormLabel
                                                        sx={isBHDiagnostic ? highlightStyle : undefined}
                                                        required={mentalHealthTreatmentHistoryInfo.RecievedMentalHealthTreatment === YES}
                                                    >
                                                        {AssessmentQuestions.MentalHealthTreatmentHistory_ReasonForTreatment}
                                                    </FormLabel>
                                                </Grid>
                                                <Grid size={6}>
                                                    <TextField
                                                        label={AssessmentQuestions.MentalHealthTreatmentHistory_ReasonForTreatment}
                                                        variant="outlined"
                                                        size="small"
                                                        multiline
                                                        rows={3}
                                                        fullWidth
                                                        margin="normal"
                                                        name="ReasonForTreatment"
                                                        value={mentalHealthTreatmentHistoryInfo.ReasonForTreatment || ''}
                                                        onChange={(e) => {
                                                            dispatch(
                                                                setMentalHealthTreatmentHistoryInfo({
                                                                    ...mentalHealthTreatmentHistoryInfo,
                                                                    ReasonForTreatment: e.target.value,
                                                                    IsReasonForTreatmentAi: false,
                                                                })
                                                            );
                                                            setIsChanged(true);
                                                        }}
                                                        InputProps={{
                                                            endAdornment: mentalHealthTreatmentHistoryInfo.IsReasonForTreatmentAi && isAIIcon(),
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid size={6}>
                                                    <FormLabel>{AssessmentQuestions.MentalHealthTreatmentHistory_DiagnosisIfKnown}</FormLabel>
                                                </Grid>
                                                <Grid size={6}>
                                                    <TextField
                                                        label={AssessmentQuestions.MentalHealthTreatmentHistory_DiagnosisIfKnown}
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        margin="normal"
                                                        name="Diagnosis"
                                                        value={mentalHealthTreatmentHistoryInfo.Diagnosis || ''}
                                                        onChange={(e) => {
                                                            dispatch(
                                                                setMentalHealthTreatmentHistoryInfo({
                                                                    ...mentalHealthTreatmentHistoryInfo,
                                                                    Diagnosis: e.target.value,
                                                                    IsDiagnosisAi: false,
                                                                })
                                                            );
                                                            setIsChanged(true);
                                                        }}
                                                        InputProps={{
                                                            endAdornment: mentalHealthTreatmentHistoryInfo.IsDiagnosisAi && isAIIcon(),
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid size={6}>
                                                    <FormLabel
                                                        sx={isBHDiagnostic ? highlightStyle : undefined}
                                                        required={mentalHealthTreatmentHistoryInfo.RecievedMentalHealthTreatment === YES}
                                                    >
                                                        {AssessmentQuestions.MentalHealthTreatmentHistory_TherapyHelpful}
                                                        {mentalHealthTreatmentHistoryInfo.IsTherapyHelpfulAi && isAIIcon()}
                                                    </FormLabel>
                                                </Grid>
                                                <Grid size={6}>
                                                    <RadioGroup
                                                        row
                                                        name="IsTherapyHelpful"
                                                        value={mentalHealthTreatmentHistoryInfo.IsTherapyHelpful || ''}
                                                        onChange={(e) => {
                                                            dispatch(
                                                                setMentalHealthTreatmentHistoryInfo({
                                                                    ...mentalHealthTreatmentHistoryInfo,
                                                                    IsTherapyHelpful: e.target.value,
                                                                    IsTherapyHelpfulAi: false,
                                                                })
                                                            );
                                                            setIsChanged(true);
                                                        }}
                                                    >
                                                        {yesNoList.map((type, index) => {
                                                            return (
                                                                <FormControlLabel
                                                                    key={`answer-label-${index}`}
                                                                    value={type.Answer}
                                                                    control={<Radio />}
                                                                    label={type.Answer}
                                                                />
                                                            );
                                                        })}
                                                    </RadioGroup>
                                                </Grid>
                                                <Grid size={6}>
                                                    <FormLabel
                                                        sx={isBHDiagnostic ? highlightStyle : undefined}
                                                        required={mentalHealthTreatmentHistoryInfo.RecievedMentalHealthTreatment === YES}
                                                    >
                                                        {AssessmentQuestions.MentalHealthTreatmentHistory_MemberComments}
                                                    </FormLabel>
                                                </Grid>
                                                <Grid size={6}>
                                                    <TextField
                                                        label={AssessmentQuestions.MentalHealthTreatmentHistory_MemberComments}
                                                        variant="outlined"
                                                        size="small"
                                                        multiline
                                                        rows={3}
                                                        fullWidth
                                                        margin="normal"
                                                        name="MemberComments"
                                                        value={mentalHealthTreatmentHistoryInfo.MemberComments || ''}
                                                        onChange={(e) => {
                                                            dispatch(
                                                                setMentalHealthTreatmentHistoryInfo({
                                                                    ...mentalHealthTreatmentHistoryInfo,
                                                                    MemberComments: e.target.value,
                                                                    IsMemberCommentsAi: false,
                                                                })
                                                            );
                                                            setIsChanged(true);
                                                        }}
                                                        InputProps={{
                                                            endAdornment: mentalHealthTreatmentHistoryInfo.IsMemberCommentsAi && isAIIcon(),
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Collapse>
                                    </Grid>

                                    <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                        <hr className="hr-lite" />
                                    </Grid>
                                    <Grid size={6}>
                                        <FormLabel sx={isBHDiagnostic ? highlightStyle : undefined} required={isBHDiagnostic}>
                                            {AssessmentQuestions.MentalHealthTreatmentHistory_PsychiatricTreatmentFacility}
                                            {mentalHealthTreatmentHistoryInfo.IsAdmittedToTreatmentFacilityAi && isAIIcon()}
                                        </FormLabel>
                                    </Grid>
                                    <Grid size={6}>
                                        <RadioGroup
                                            row
                                            name="AdmittedToTreatmentFacility"
                                            value={mentalHealthTreatmentHistoryInfo.AdmittedToTreatmentFacility || ''}
                                            onChange={(e) => {
                                                dispatch(
                                                    setMentalHealthTreatmentHistoryInfo({
                                                        ...mentalHealthTreatmentHistoryInfo,
                                                        AdmittedToTreatmentFacility: e.target.value,
                                                        IsAdmittedToTreatmentFacilityAi: false,
                                                    })
                                                );
                                                setIsChanged(true);
                                            }}
                                        >
                                            {yesNoList.map((type, index) => {
                                                return (
                                                    <FormControlLabel
                                                        key={`answer-label-${index}`}
                                                        value={type.Answer}
                                                        control={<Radio />}
                                                        label={type.Answer}
                                                    />
                                                );
                                            })}
                                        </RadioGroup>
                                    </Grid>
                                    <Grid size={12}>
                                        <Collapse
                                            in={mentalHealthTreatmentHistoryInfo.AdmittedToTreatmentFacility === YES}
                                            timeout="auto"
                                            unmountOnExit
                                            sx={{ width: '100%' }}
                                        >
                                            <Box style={{ height: '100%', textAlign: 'right' }}>
                                                <Button
                                                    className="button-120"
                                                    variant="contained"
                                                    onClick={() => {
                                                        addHospitalizationHandler();
                                                    }}
                                                >
                                                    Add Hospitalization
                                                </Button>
                                                <DataGrid
                                                    autoHeight
                                                    getRowId={(row) => row.Id || row.Facility?.Id}
                                                    rows={mentalHealthTreatmentHistoryInfo.MemberHospitalizations?.filter((h) => !h.IsDeleted) || []}
                                                    columns={hospitalizationColumns}
                                                    experimentalFeatures={{ newEditingApi: true } as any}
                                                    hideFooter
                                                />
                                            </Box>
                                        </Collapse>
                                    </Grid>
                                    <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                        <hr className="hr-lite" />
                                    </Grid>
                                    <Grid size={6}>
                                        <FormLabel sx={isBHDiagnostic ? highlightStyle : undefined} required={isBHDiagnostic}>
                                            {AssessmentQuestions.MentalHealthTreatmentHistory_SMIDesignation}
                                            {mentalHealthTreatmentHistoryInfo.IsAssessedForSMIAi && isAIIcon()}
                                        </FormLabel>
                                    </Grid>
                                    <Grid size={6}>
                                        <RadioGroup
                                            row
                                            name="AssessedForSMI"
                                            value={mentalHealthTreatmentHistoryInfo.AssessedForSMI || ''}
                                            onChange={(e) => {
                                                dispatch(
                                                    setMentalHealthTreatmentHistoryInfo({
                                                        ...mentalHealthTreatmentHistoryInfo,
                                                        AssessedForSMI: e.target.value,
                                                        IsAssessedForSMIAi: false,
                                                    })
                                                );
                                                setIsChanged(true);
                                            }}
                                        >
                                            {yesNoList.map((type, index) => {
                                                return (
                                                    <FormControlLabel
                                                        key={`answer-label-${index}`}
                                                        value={type.Answer}
                                                        control={<Radio />}
                                                        label={type.Answer}
                                                    />
                                                );
                                            })}
                                        </RadioGroup>
                                        <Collapse in={mentalHealthTreatmentHistoryInfo.AssessedForSMI === NO} timeout="auto">
                                            <Grid size={12}>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={4}
                                                    variant="outlined"
                                                    label="Comments"
                                                    required={mentalHealthTreatmentHistoryInfo.AssessedForSMI === NO}
                                                    name="AssessedForSMIComments"
                                                    value={mentalHealthTreatmentHistoryInfo.AssessedForSMIComments || ''}
                                                    onChange={(e) => {
                                                        dispatch(
                                                            setMentalHealthTreatmentHistoryInfo({
                                                                ...mentalHealthTreatmentHistoryInfo,
                                                                AssessedForSMIComments: e.target.value,
                                                                IsAssessedForSMICommentsAi: false,
                                                            })
                                                        );
                                                        setIsChanged(true);
                                                    }}
                                                    InputProps={{
                                                        endAdornment: mentalHealthTreatmentHistoryInfo.IsAssessedForSMICommentsAi && isAIIcon(),
                                                    }}
                                                />
                                            </Grid>
                                        </Collapse>
                                    </Grid>
                                </Grid>

                                <Grid justifyContent="flex-end" className="content-footer">
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ marginRight: 2 }}
                                        disabled={isSaving}
                                        onClick={() => dispatch(gotoPreviousMenu())}
                                        startIcon={<ArrowBackIosIcon />}
                                    >
                                        Previous
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={isSaving}
                                        endIcon={isSaving ? <CircularProgress size={18} /> : <ArrowForwardIosIcon />}
                                        onClick={handleSubmit}
                                    >
                                        Next
                                    </Button>
                                </Grid>
                            </>
                        )}

                        {Boolean(
                            mentalHealthTreatmentHistoryInfoIsComplete(mentalHealthTreatmentHistoryInfo) &&
                                mentalHealthTreatmentHistoryInfo.CreatedBy &&
                                !isChanged
                        ) ? (
                            <AlertDialog
                                open={showSaveAlert}
                                title={`Mental Health Treatment History`}
                                buttonsDirection="column-reverse"
                                showBottomCenter={true}
                                cancelText="Go Back"
                                skipText={`Mental Health Treatment History Assessment Not Reviewed`}
                                okText={`Agree With Mental Health Treatment History Assessment`}
                                onConfirm={() => handleOnConfirmToNext(mentalHealthTreatmentHistoryInfo, true)}
                                onSkip={handleOnConfirmSkip}
                                onClose={() => setShowSaveAlert(false)}
                                isLoading={isSaving}
                                message={`Last Mental Health Treatment History assessment completed on ${(() => {
                                    const d = toDate(mentalHealthTreatmentHistoryInfo.UpdatedDate ?? mentalHealthTreatmentHistoryInfo.CreatedDate);
                                    return d ? formatDate(d, 'MM/dd/yyyy') : '';
                                })()}.`}
                            />
                        ) : Boolean(mentalHealthTreatmentHistoryInfoIsComplete(mentalHealthTreatmentHistoryInfo)) ? (
                            <AlertDialog
                                open={showSaveAlert}
                                title="Mental Health Treatment History"
                                cancelText="Go Back"
                                okText="Confirm"
                                onConfirm={() => handleOnConfirmToNext(mentalHealthTreatmentHistoryInfo, true)}
                                onSkip={handleOnConfirmSkip}
                                showBottomCenter={true}
                                onClose={() => setShowSaveAlert(false)}
                                isLoading={isSaving}
                                message="I confirm and acknowledge that Mental Health Treatment History is completed."
                            />
                        ) : (
                            <AlertDialog
                                open={showSaveAlert}
                                title="Mental Health Treatment History"
                                showBottomCenter={true}
                                cancelText="Go Back"
                                skipText="Skip and continue"
                                okText="Confirm"
                                onConfirm={() => handleOnConfirmToNext(mentalHealthTreatmentHistoryInfo, false)}
                                onSkip={handleOnConfirmSkip}
                                onClose={() => setShowSaveAlert(false)}
                                isLoading={isSaving}
                                renderBody={() =>
                                    BHDiagnosticConfirmationBody({
                                        assessmentName: 'Mental Health Treatment History',
                                        diagnosticType: engagementDraft.DiagnosticStatus?.DiagnosticType ?? DiagnosticType.None,
                                    })
                                }
                            />
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </>
    );
};
export default MentalHealthTreatmentHistory;
